import React from "react";

import { noop } from "lodash";
import moment from "moment";
import uuidv4 from "uuid/v4";

import type { UserPresence } from "~/lib/prose/plugins/multicursor";
import type { CopyRecapOptions, RecapSection } from "~/meetingRecorder/copyUtils";

import type { AttendeeAttrs, AttendeesPresenceInfo } from "../common/Attendees";

interface TranscriptModalContextInterface {
	noteId: string | null;
	setNoteId: (noteId: string | null) => void;
	meetingRecordingSummaryId?: string | null;
	setMeetingRecordingSummaryId: (meetingRecordingSummaryId?: string) => void;
	analyticsFingerprint: string;
	attendeesPresenceInfo: AttendeesPresenceInfo;
	anonymousAttendees: AttendeeAttrs[];
	onUserPresenceChange: (presence: UserPresence, addUserToAnonymous: boolean) => void;
	timestampStart?: number | null;
	timestampEnd?: number | null;
}

export const TranscriptModalContext = React.createContext<TranscriptModalContextInterface>({
	noteId: null,
	setNoteId: noop,
	meetingRecordingSummaryId: undefined,
	analyticsFingerprint: uuidv4(),
	setMeetingRecordingSummaryId: noop,
	attendeesPresenceInfo: {},
	anonymousAttendees: [],
	onUserPresenceChange: noop,
	timestampStart: undefined,
	timestampEnd: undefined,
});

const analyticsFingerprint = uuidv4();

interface TranscriptModalProviderProps {
	timestampStart?: number | null;
	timestampEnd?: number | null;
	children: React.ReactNode;
}

export const TranscriptModalContextProvider: React.FC<TranscriptModalProviderProps> = props => {
	const { timestampStart, timestampEnd } = props;
	const [noteId, setNoteId] = React.useState<string | null>(null);
	const [meetingRecordingSummaryId, setMeetingRecordingSummaryId] = React.useState<string | undefined>(undefined);

	const [attendeesPresenceInfo, setAttendeesPresenceInfo] = React.useState<AttendeesPresenceInfo>({});
	const [anonymousAttendees, setAnonymousAttendees] = React.useState<AttendeeAttrs[]>([]);

	const onUserPresenceChange = React.useCallback(
		(presence: UserPresence, addUserToAnonymous: boolean) => {
			const { sessionId, user, lastActive, isCurrentlyViewing, platform } = presence;

			if (addUserToAnonymous) {
				let newAnonymousAttendees = [...anonymousAttendees];
				const isPresentInAnonymousList = anonymousAttendees.some(
					anonymousAttendee => anonymousAttendee.email === user.email,
				);
				if (!isCurrentlyViewing && isPresentInAnonymousList) {
					newAnonymousAttendees = anonymousAttendees.filter(
						anonymousAttendee => anonymousAttendee.email !== user.email,
					);
				} else if (isCurrentlyViewing && !isPresentInAnonymousList) {
					newAnonymousAttendees = anonymousAttendees.concat([
						{
							id: user.id,
							email: user.email,
							image: user.image ?? "",
							fullName: user.fullName,
							user: user,
							invitable: false,
							__typename: "Anonymous",
							isGuest: false,
							type: "GUEST",
							isAnonymous: true,
							isExternal: user.isExternal ?? false,
						},
					]);
				}
				setAnonymousAttendees(newAnonymousAttendees);
			}

			setAttendeesPresenceInfo(prev => {
				const userSessions = prev[user.id];
				const newUserSessions = [
					{
						sessionId,
						isCurrentlyViewing: !!isCurrentlyViewing,
						lastActiveDatetime: lastActive ? moment(lastActive).format() : undefined,
						platform: platform,
					},
					...(userSessions ?? []).filter(s => s.sessionId !== sessionId),
				];

				return {
					...prev,
					[user.id]: newUserSessions,
				};
			});
		},
		[anonymousAttendees],
	);

	const value = React.useMemo(
		() => ({
			noteId,
			setNoteId,
			meetingRecordingSummaryId,
			setMeetingRecordingSummaryId,
			analyticsFingerprint,
			attendeesPresenceInfo,
			anonymousAttendees,
			onUserPresenceChange,
			timestampStart,
			timestampEnd,
		}),
		[
			noteId,
			meetingRecordingSummaryId,
			attendeesPresenceInfo,
			anonymousAttendees,
			onUserPresenceChange,
			timestampStart,
			timestampEnd,
		],
	);

	return <TranscriptModalContext.Provider value={value}>{props.children}</TranscriptModalContext.Provider>;
};

interface RecordingContextInterface {
	recordingId: string | null;
	copySection: (section: RecapSection, options?: CopyRecapOptions) => void;
	openClipCutModal: (initialTimestamp?: number, initialEndTimestamp?: number) => void;
}

export const RecordingContext = React.createContext<RecordingContextInterface>({
	recordingId: null,
	copySection: noop,
	openClipCutModal: noop,
});
